import { z } from 'zod';

/**
 * Application configuration, read from Vite's compile-time environment.
 *
 * Parsed at module load and thrown on immediately if invalid: a missing
 * VITE_API_BASE_URL would otherwise surface much later as a confusing 404 to
 * `undefined/auth/login`. Failing at startup with a readable message is
 * cheaper to diagnose than a broken request.
 */
const envSchema = z.object({
  VITE_API_BASE_URL: z
    .string()
    .min(1, 'VITE_API_BASE_URL is required (e.g. "/api/v1" or "https://api.example.com/api/v1").')
    // A trailing slash would produce "//auth/login" once paths are appended.
    .transform((value) => value.replace(/\/+$/, '')),
  VITE_APP_NAME: z.string().min(1).default('Client Portal'),
  VITE_SUPPORT_EMAIL: z
    .email()
    .optional()
    .or(z.literal('').transform(() => undefined)),
  VITE_ENABLE_QUERY_DEVTOOLS: z
    .string()
    .optional()
    .transform((value) => value === 'true'),
});

const parsed = envSchema.safeParse(import.meta.env);

if (!parsed.success) {
  const details = parsed.error.issues
    .map((issue) => `  - ${issue.path.join('.') || '(root)'}: ${issue.message}`)
    .join('\n');

  throw new Error(
    `Invalid frontend environment configuration:\n${details}\n\n` +
      'Copy .env.example to .env.local and fill in the missing values.',
  );
}

export interface AppConfig {
  /** Base URL of the API, including the /api/v1 prefix, without a trailing slash. */
  readonly apiBaseUrl: string;
  readonly appName: string;
  readonly supportEmail: string | undefined;
  readonly enableQueryDevtools: boolean;
  readonly isProduction: boolean;
}

export const config: AppConfig = Object.freeze({
  apiBaseUrl: parsed.data.VITE_API_BASE_URL,
  appName: parsed.data.VITE_APP_NAME,
  supportEmail: parsed.data.VITE_SUPPORT_EMAIL,
  // Devtools are opt-in AND never bundled into a production build.
  enableQueryDevtools: parsed.data.VITE_ENABLE_QUERY_DEVTOOLS && import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
});
